/* Overview — what this salon is, and whether it is working.
 *
 * The first page inside a tenant, so it answers the two questions that bring
 * somebody here: who are they, and what is wrong. Everything on it is either an
 * identity fact (name, note, id, when they started) or a reading taken from
 * somewhere else in the app, with a way to jump to the page that owns it.
 *
 * It edits almost nothing on purpose. The name and the note are here because
 * they are the tenant row itself and there is nowhere else for them; every other
 * control on this page is a link.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { nav } from '../nav.js';
import { store } from '../store.js';
import { ConfirmButton } from '../components/confirm-button.js';

var Overview = {
  name: 'AdminOverview',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var name = Vue.ref('');
    var note = Vue.ref('');
    var recent = Vue.ref([]);
    var supportMinutes = Vue.ref(30);
    var supportReason = Vue.ref('');

    // Seeded from the row rather than bound to it: a text box bound straight to
    // the store would rewrite the tenant on every keystroke, and would also jump
    // under the cursor when the list reloads behind it.
    Vue.watchEffect(function () {
      var t = store.selected.value;
      if (!t) return;
      name.value = t.name;
      note.value = t.note || '';
    });

    async function loadRecent() {
      var id = store.selectedId.value;
      if (!id) return;
      var rows = await api.query('Loading activity', function (sb) {
        return sb.from('admin_audit').select('*')
          .eq('tenant_id', id).order('at', { ascending: false }).limit(6);
      });
      if (rows) recent.value = rows;
    }

    Vue.watch(store.selectedId, loadRecent, { immediate: true });

    async function saveName() {
      var v = name.value.trim();
      if (!v || v === (store.selected.value && store.selected.value.name)) return;
      if (await store.saveTenant({ name: v })) {
        api.toast('Renamed. The console shows whatever the salon set under Branding, not this.');
        loadRecent();
      }
    }

    async function saveNote() {
      var v = note.value.trim();
      if (v === ((store.selected.value && store.selected.value.note) || '')) return;
      await store.saveTenant({ note: v || null });
    }

    // Where each reading comes from, and what it means when it is bad. Written
    // as data so the markup is a loop rather than eight nearly identical blocks.
    var checks = Vue.computed(function () {
      var t = store.selected.value;
      if (!t) return [];
      var lic = store.entitlement.value;
      return [
        {
          // First, and above People, because it is the only reading here that
          // can be wrong while everything else is right: a salon with staff,
          // a synced book and a working mailbox still shows a card instead of
          // a board if this has run out.
          label: 'Licence',
          value: lic.state === 'active'
            ? (lic.planName + (lic.expiresAt ? ' · ' + lic.left.label : ''))
            : (lic.state === 'lapsed' ? 'lapsed' : 'none'),
          kind: lic.state !== 'active' ? 'bad' : (lic.expiring ? 'warn' : 'ok'),
          note: lic.state === 'none'
            ? 'Nothing has been granted — their console offers nothing at all.'
            : (lic.state === 'lapsed'
                ? 'The term ended. Every row they wrote is still there.'
                : lic.features.length + ' features, ' + store.seats.value.label + '.'),
          page: 'licensing'
        },
        {
          label: 'People',
          value: t.member_count + (t.member_count === 1 ? ' account' : ' accounts'),
          kind: t.member_count ? 'ok' : 'bad',
          note: t.member_count ? 'Can sign a station in.' : 'Nobody can sign in to this salon.',
          page: 'members'
        },
        {
          label: 'Booking system',
          value: t.scheduling_on ? (t.scheduling_plugin || 'on') : 'off',
          kind: t.scheduling_on ? 'ok' : 'warn',
          note: t.scheduling_on
            ? t.mapped_count + ' services mapped to a turn type.'
            : 'The board knows nothing about appointments.',
          page: 'appointments'
        },
        {
          label: 'Book synced',
          value: store.ago(t.last_synced_at),
          kind: t.last_synced_at ? 'ok' : 'warn',
          note: t.appointment_count + ' appointments mirrored.',
          page: 'ingest'
        },
        {
          label: 'Ingest keys',
          value: t.ingest_key_count ? t.ingest_key_count + ' active' : 'none',
          kind: t.ingest_key_count ? 'ok' : 'warn',
          note: t.ingest_last_seen_at
            ? 'Last used ' + store.ago(t.ingest_last_seen_at) + '.'
            : 'No webhook has ever presented one.',
          page: 'ingest'
        },
        {
          label: 'Board in use',
          value: store.ago(t.last_turn_at),
          kind: t.last_turn_at ? 'ok' : 'warn',
          note: t.turn_count + ' turns across ' + t.day_count + ' days.',
          page: 'danger'
        }
      ];
    });

    /* ---------- support sessions ----------
     *
     * Starting one does not change this app; it changes the CONSOLE, for this
     * admin, for as long as it lasts. See the header of js/store.js.
     */
    async function startSupport() {
      var t = store.selected.value;
      if (!t) return;
      var okay = await store.startSupport(t.id, supportMinutes.value, supportReason.value.trim());
      if (!okay) return;
      supportReason.value = '';
      loadRecent();
      openConsole();
    }

    // A new tab rather than this one: the admin app is where the session is
    // ended, and navigating away from it to use the session is how somebody
    // ends up with no way back to the button that stops it.
    function openConsole() {
      if (!api.consoleUrl) {
        api.toast('Session started. Open the console and sign in with your admin account.');
        return;
      }
      window.open(api.consoleUrl, '_blank', 'noopener');
    }

    async function stopSupport() {
      if (await store.stopSupport()) loadRecent();
    }

    return {
      t: store.selected,
      name: name, note: note,
      saveName: saveName, saveNote: saveNote,
      checks: checks,
      recent: recent,
      stamp: store.stamp,
      ago: store.ago,
      goPage: nav.goPage,
      supportMinutes: supportMinutes,
      supportReason: supportReason,
      supportChoices: store.supportChoices,
      supporting: store.supporting,
      supportingThis: store.supportingSelected,
      supportLeft: store.supportLeft,
      startSupport: startSupport,
      stopSupport: stopSupport,
      openConsole: openConsole,
      consoleUrl: api.consoleUrl
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Overview</h2>',
    '    <p class="page__lead">What this salon is and whether anything about it needs',
    '      attention. Everything here is read from elsewhere in the app &mdash; the links',
    '      go to the page that owns it.</p>',
    '  </div>',
    '',
    '  <p v-if="!t" class="empty">Loading…</p>',
    '  <template v-else>',
    '',
    '  <div class="card">',
    '    <h3>Identity</h3>',
    '    <p class="hint">The tenant&rsquo;s own name, used in this app and nowhere else. What',
    '      the console&rsquo;s top bar says is the salon&rsquo;s <strong>brand</strong>, set',
    '      under Branding &mdash; the two are allowed to differ and often should.</p>',
    '    <div class="card__row">',
    '      <label class="field field--grow">',
    '        <span class="field__label">Tenant name</span>',
    '        <input v-model="name" class="input" type="text" maxlength="80"',
    '          @change="saveName" @keyup.enter="$event.target.blur()" />',
    '      </label>',
    '      <label class="field field--grow">',
    '        <span class="field__label">Note</span>',
    '        <input v-model="note" class="input" type="text" maxlength="200"',
    '          placeholder="Anything worth remembering" @change="saveNote" />',
    '      </label>',
    '    </div>',
    '    <dl class="facts">',
    '      <div><dt>Tenant id</dt><dd class="mono">{{ t.id }}</dd></div>',
    '      <div><dt>Created</dt><dd>{{ stamp(t.created_at) }}</dd></div>',
    '      <div><dt>Suspended</dt>',
    '        <dd>{{ t.suspended_at ? stamp(t.suspended_at) : "No — live" }}</dd></div>',
    '      <div><dt>Settings rows</dt><dd>{{ t.setting_count }} of 10 known keys</dd></div>',
    '    </dl>',
    '  </div>',
    '',
    '  <div class="card" :class="supportingThis ? \'card--reveal\' : \'\'">',
    '    <div class="card__head">',
    '      <h3>Log in as this salon</h3>',
    '      <span v-if="supportingThis" class="tag tag--set">session live</span>',
    '    </div>',
    '',
    /* Already in this salon. */
    '    <template v-if="supportingThis">',
    '      <p class="hint">Your console session <strong>is</strong> this salon for the next',
    '        <strong>{{ supportLeft.label }}</strong>. Sign in to the console with your own admin',
    '        account and you get their board &mdash; their turns, their staff, their settings.</p>',
    '      <div class="card__row">',
    '        <button type="button" class="btn btn--primary" @click="openConsole">',
    '          {{ consoleUrl ? "Open the console" : "How to open it" }}</button>',
    '        <button type="button" class="btn btn--ghost" @click="stopSupport">End session now</button>',
    '      </div>',
    '      <p class="hint">It ends on its own at <strong>{{ stamp(supporting.expiresAt) }}</strong>.',
    '        After that the console falls back to whichever salon your own account belongs to,',
    '        or to none &mdash; the board will change under you rather than stop.</p>',
    '    </template>',
    '',
    /* In a DIFFERENT salon. One at a time, like every account in the product. */
    '    <template v-else-if="supporting">',
    '      <p class="warn-line">You are already signed in to the console as',
    '        <strong>{{ supporting.tenantName }}</strong> ({{ supportLeft.label }} left).</p>',
    '      <p class="hint">An account is one salon at a time &mdash; the same rule the whole',
    '        schema rests on. Starting a session here moves you, it does not add one.</p>',
    '      <confirm-button label="Move to this salon" kind="quiet"',
    '        :confirm="\'Leave \' + supporting.tenantName + \' and support this one\'"',
    '        @confirmed="startSupport" />',
    '    </template>',
    '',
    '    <template v-else-if="t && t.suspended_at">',
    '      <p class="hint">This salon is <strong>suspended</strong>, so nobody can be signed in',
    '        as it &mdash; including us. Suspension is meant to be absolute; lift it from the',
    '        <button type="button" class="link-btn" @click="goPage(\'danger\')">Danger zone</button>',
    '        first.</p>',
    '    </template>',
    '',
    '    <template v-else>',
    '      <p class="hint">Puts <strong>your console session</strong> into this salon for a fixed',
    '        time: sign in to the console with your own admin account and you see exactly what',
    '        they see. Nothing about this admin app changes &mdash; it already reaches every',
    '        salon.</p>',
    '      <p class="hint">Anything you do in there is written as the salon, not as you. The log',
    '        records that a session was started, by whom and why; it cannot record what was done',
    '        inside it, because a turn has no author. Treat it as sitting at their front desk.</p>',
    '      <div class="card__row">',
    '        <div class="pick" role="radiogroup" aria-label="How long">',
    '          <button v-for="m in supportChoices" :key="m" type="button" class="pick__opt"',
    '            :class="{ \'is-on\': supportMinutes === m }" role="radio"',
    '            :aria-checked="supportMinutes === m ? \'true\' : \'false\'"',
    '            @click="supportMinutes = m">{{ m < 60 ? m + " min" : (m / 60) + "h" }}</button>',
    '        </div>',
    '        <label class="field field--grow">',
    '          <span class="field__label">Why (recorded in the log)</span>',
    '          <input v-model="supportReason" class="input" type="text" maxlength="120"',
    '            placeholder="What they called about" />',
    '        </label>',
    '        <button type="button" class="btn btn--primary" @click="startSupport">',
    '          Start a support session</button>',
    '      </div>',
    '    </template>',
    '  </div>',
    '',
    '  <h3 class="page__sub">Health</h3>',
    '  <ul class="checks">',
    '    <li v-for="c in checks" :key="c.label" class="check">',
    '      <span class="check__dot" :class="\'is-\' + c.kind"></span>',
    '      <span class="check__main">',
    '        <span class="check__label">{{ c.label }}</span>',
    '        <span class="check__note">{{ c.note }}</span>',
    '      </span>',
    '      <span class="check__value">{{ c.value }}</span>',
    '      <button type="button" class="link-btn" @click="goPage(c.page)">Open</button>',
    '    </li>',
    '  </ul>',
    '',
    '  <div class="card">',
    '    <h3>Recent admin activity</h3>',
    '    <p class="hint">What has been done to this salon from this app. The full log,',
    '      including who did it, is under <strong>Activity</strong>.</p>',
    '    <p v-if="!recent.length" class="empty">Nothing has been changed from here yet.</p>',
    '    <ul v-else class="log">',
    '      <li v-for="row in recent" :key="row.id" class="log__row">',
    '        <span class="log__at" :title="stamp(row.at)">{{ ago(row.at) }}</span>',
    '        <span class="log__action">{{ row.action }}</span>',
    '        <span class="log__who">{{ row.actor_email }}</span>',
    '      </li>',
    '    </ul>',
    '  </div>',
    '',
    '  </template>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'overview',
  name: 'Overview',
  group: 'Salon',
  page: Overview
});

export { Overview };
